import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "sonner";
import AppShell from "@/components/AppShell";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PatientDashboard from "@/pages/PatientDashboard";
import SessionsList from "@/pages/SessionsList";
import ExercisePlayer from "@/pages/ExercisePlayer";
import ProfessionalPanel from "@/pages/ProfessionalPanel";
import PatientDetail from "@/pages/PatientDetail";
import Settings from "@/pages/Settings";

import AdminUsers from "@/pages/AdminUsers";
import AdminInvitations from "@/pages/AdminInvitations";
import AdminAudit from "@/pages/AdminAudit";
import InviteRegister from "@/pages/InviteRegister";
import TodayTraining from "@/pages/TodayTraining";
import History from "@/pages/History";
import Research from "@/pages/Research";
import Taxonomy from "@/pages/Taxonomy";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-8">Cargando...</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
}

function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <Landing />;
  if (user.role === "admin") return <Navigate to="/admin/users" replace />;
  return <Navigate to={user.role === "patient" ? "/dashboard" : "/pro"} replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/invite/:token" element={<InviteRegister />} />

            <Route path="/dashboard" element={<Protected roles={["patient", "researcher"]}><PatientDashboard /></Protected>} />
            <Route path="/today" element={<Protected roles={["patient", "researcher"]}><TodayTraining /></Protected>} />
            <Route path="/today/play" element={<Protected roles={["patient", "researcher"]}><ExercisePlayer /></Protected>} />
            <Route path="/history" element={<Protected roles={["patient", "researcher"]}><History /></Protected>} />
            <Route path="/sessions" element={<Protected roles={["patient", "researcher"]}><SessionsList /></Protected>} />
            <Route path="/sessions/:sessionId" element={<Protected><ExercisePlayer /></Protected>} />
            <Route path="/assessment/:kind" element={<Protected><ExercisePlayer /></Protected>} />
            <Route path="/review" element={<Protected><ExercisePlayer /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />

            <Route path="/pro" element={<Protected roles={["professional", "admin"]}><ProfessionalPanel /></Protected>} />
            <Route path="/pro/patients/:id" element={<Protected roles={["professional", "admin", "researcher"]}><PatientDetail /></Protected>} />
            <Route path="/admin/users" element={<Protected roles={["admin"]}><AdminUsers /></Protected>} />
            <Route path="/admin/invitations" element={<Protected roles={["admin"]}><AdminInvitations /></Protected>} />
            <Route path="/admin/audit" element={<Protected roles={["admin"]}><AdminAudit /></Protected>} />
            <Route path="/research" element={<Protected roles={["admin", "researcher", "professional"]}><Research /></Protected>} />
            <Route path="/taxonomy" element={<Protected><Taxonomy /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
